import { Form, Space, Typography } from "antd";
import { FormInputPassword } from "../form";
import { RefObject, useEffect, useState } from "react";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { Modal, type ModalHandle } from "../modal";
import { ModalCloseButton, SubmitButton } from "../operation";
import {
  useActiveAuthProvider,
  useLogout,
  useUpdatePassword,
} from "@refinedev/core";
import type { Validator } from "../form/types";
import { useMessage } from "utils";

const { Text } = Typography;
interface ChangePasswordForm {
  password: string;
  newPassword: string;
}

const checkPasswordComplexity = (value: string) => {
  const hasNumber = /\d/.test(value);
  const hasUpperCase = /[A-Z]/.test(value);
  const hasLowerCase = /[a-z]/.test(value);
  const hasSpecialChar = /[!@#$%^&*]/.test(value);
  const valid =
    (hasNumber && (hasUpperCase || hasLowerCase || hasSpecialChar)) ||
    (hasUpperCase && (hasNumber || hasLowerCase || hasSpecialChar)) ||
    (hasLowerCase && (hasNumber || hasUpperCase || hasSpecialChar)) ||
    (hasSpecialChar && (hasNumber || hasUpperCase || hasLowerCase));
  return valid;
};
export const ChangePassword = forwardRef<HTMLButtonElement>((_, ref) => {
  const [isNewPasswordFieldTouched, setIsNewPasswordFieldTouched] =
    useState(false);

  const modalRef = useRef<ModalHandle>(null);
  const authProvider = useActiveAuthProvider();
  const { mutateAsync: mutateUpdatePasswordAsync, isLoading } =
    useUpdatePassword({
      v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
    });
  const { mutate: logout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  });
  const { message, contextHolder } = useMessage();
  const [form] = Form.useForm<ChangePasswordForm>();
  const newPassword = Form.useWatch("newPassword", form);

  useEffect(() => {
    if (newPassword && !isNewPasswordFieldTouched) {
      setIsNewPasswordFieldTouched(true);
    }
  }, [newPassword, isNewPasswordFieldTouched]);

  useImperativeHandle(
    ref,
    // @ts-ignore
    () => ({
      click: () => modalRef.current?.open(),
    }),
    []
  );

  const open = () => {
    modalRef.current?.open();
  };

  const footer = (
    <Space>
      <ModalCloseButton modalRef={modalRef} />
      <SubmitButton form={form} isLoading={isLoading} buttonName="confirm" />
    </Space>
  );
  const validatePassword: Validator = (rule, value, callback) => {
    const valid = checkPasswordComplexity(value);

    if (!valid) {
      callback("パスワードの形式が正しくありません。");
      return;
    }

    callback();
  };
  const validateNewPassword: Validator = (rule, value, callback) => {
    const valid = checkPasswordComplexity(value);
    if (!valid) {
      callback("パスワードの形式が正しくありません。");
      return;
    }
    if (value && form.getFieldValue("password") === value) {
      callback("新しいパスワードは古いパスワードと同じであってはいけません。");
      return;
    }

    callback();
  };
  const validateConfirmPassword: Validator = (rule, value, callback) => {
    const valid = checkPasswordComplexity(value);
    if (!valid) {
      callback("パスワードの形式が正しくありません。");
      return;
    }
    if (value && form.getFieldValue("newPassword") !== value) {
      callback("二回入力されたパスワードが一致していません。");
      return;
    }

    callback();
  };

  const onFinish = async (values: any) => {
    await mutateUpdatePasswordAsync(values);
    message.success("パスワードの変更が成功しました。");
    logout();
    modalRef.current?.close();
  };

  return (
    <>
      {contextHolder}
      <Text onClick={open}>修改密码</Text>
      <Modal ref={modalRef} title="情報" footer={footer}>
        <Form form={form} labelCol={{ span: 8 }} labelWrap onFinish={onFinish}>
          <FormInputPassword
            name="password"
            label="以前のパスワード"
            rules={[
              { required: true, min: 10, max: 50 },
              { validator: validatePassword },
            ]}
          />
          <FormInputPassword
            name="newPassword"
            label="新しいパスワード"
            extra="パスワードは10〜50桁で、アルファベットの大文字と数字と記号の入力が必要です。"
            rules={[
              { required: true, min: 10, max: 50 },
              { validator: validateNewPassword },
            ]}
          />
          <FormInputPassword
            name="confirmPassword"
            label="パスワードの確認"
            dependencies={["newPassword"]}
            rules={[{ required: true }, { validator: validateConfirmPassword }]}
            hidden={!isNewPasswordFieldTouched}
          />
        </Form>
      </Modal>
    </>
  );
});
